// inspo taken from https://github.com/joschan21/image-alt-generator/blob/main/src/components/ui/button.tsx

import * as React from "react";
// for typescript
import { VariantProps, cva } from "class-variance-authority";
import { concatenateClassnames } from "../../lib/utilities";

type ButtonVariants = {
  [key: string]: string;
};

const variant: ButtonVariants = {
  exit: "flex h-5 w-5 items-center justify-center rounded text-cubeBase-600 hover:cursor-pointer hover:bg-ButtonColour-100 hover:text-black dark:text-white dark:hover:bg-red-500 dark:hover:text-white",
  favourite:
    "flex h-4 w-4 items-center justify-center rounded text-cubeBase-600 hover:cursor-pointer hover:bg-yellow-400 hover:text-black dark:text-white dark:hover:bg-yellow-500 dark:hover:text-white",
  add: "flex h-5 w-5 bg-transparent dark:bg-transparent items-center justify-center rounded text-cubeBase-600 hover:cursor-pointer hover:bg-slate-300 hover:text-black dark:text-white dark:hover:bg-gray-900 dark:hover:text-white",

  icon: "rounded bg:transparent dark:bg-transparent dark:hover:bg-transparent hover:bg-transparent justify-center items-center flex text-cubeBase-600 hover:text-orange-600 dark:text-cubeBaseDark-600 dark:hover:text-orange-400",
  toastAction:
    "mb-2 w-max whitespace-nowrap rounded bg-[#2e2e2e] px-4 py-1 text-white dark:text-white hover:bg-[#3a3a3a] dark:bg-cubeBaseDark-700 dark:hover:bg-cubeBaseDark-600",
  modalTabActive:
    "dark:bg-sky-40 hover:dark:bg-sky-40 hover:bg-sky-300 bg-sky-300 text-white dark:text-black rounded text-left capitalize ",
  modalTabInactive:
    "bg-cubeBase-900 hover:bg-[#7b7b7b95] text-black dark:text-white dark:bg-cubeBaseDark-600 dark:hover:bg-[#7b7b7b95]  rounded text-left capitalize ",
  sidebarItem:
    "flex w-full items-center rounded-r-lg px-1 text-gray-900  hover:bg-cubeBase-700 dark:text-white dark:hover:bg-cubeBaseDark-700 border-black dark:border-gray-400",
  hoverMenu:
    "z-40 flex  w-full gap-2 p-2 text-left text-sm text-black dark:text-gray-200 hover:dark:bg-[#222222] hover:bg-[#F2F3F3]",
  mainBlueAction:
    "rounded bg-[#BFDFF7] dark:text-black text-[#404040] hover:bg-[#a4d1f4]  dark:bg-[#5DA2D2] dark:hover:bg-[#378BC5]",
  // Log in and send to panel.
  mainOrangeAction:
    "rounded  border-orange-400 bg-orange-300  hover:bg-orange-400 dark:bg-[#D37F45] text-black dark:text-black dark:hover:bg-orange-500",
  secondaryBlueAction:
    "rounded bg-gray-100 text-inherit hover:dark:text-[black] dark:text-inherit hover:bg-[#87D4FD] dark:bg-[#1B1A1A] dark:border-[#5DA2D2]  dark:hover:bg-[#5DA2D2]",
  secondaryBlueActionNoHover:
    "rounded bg-gray-100 text-inherit hover:dark:text-inherit dark:text-inherit hover:bg-gray-100 dark:bg-[#1B1A1A] dark:border-[#5DA2D2]  dark:hover:bg-[#1B1A1A]",
  secondaryBlueActionAtTradeCreation:
    "rounded bg-gray-100 text-inherit hover:dark:text-[black] dark:text-inherit hover:bg-[#87D4FD] dark:bg-[#303030] dark:hover:bg-[#5DA2D2]",

  settingsAction:
    "rounded-md bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600",
  sidebarButton:
    "m-1 flex h-fit w-full flex-col justify-center dark:text-white border bg-slate-200 p-1 hover:border-black hover:bg-slate-300 dark:border-black dark:bg-slate-700 dark:hover:border-white dark:hover:bg-slate-900",
  // -- //
  // Note: the two config items are overwritting the AG-Grid values. As a result of this, they need a !bg. There is also a normal dark:bg to also satisfy the testing.
  configItemActive:
    "w-full !bg-sky-200 text-black dark:text-black dark:!bg-[#5DA2D2] hover:bg-sky-200 dark:hover:bg-sky-500 overflow-hidden whitespace-nowrap flex items-center",
  // Similar stying to modalTabInactive. Colud be combined.
  configItemInactive:
    "w-full bg-gray-50 dark:bg-gray-600 text-black dark:text-white hover:bg-[#7b7b7b95] dark:hover:bg-[#7b7b7b95] overflow-hidden whitespace-nowrap flex items-center",
};

const variantToggle: ButtonVariants = {
  selectionActive:
    "font-semibold  bg-[#BFDFF7] text-black dark:text-black hover:bg-[#a4d1f4] dark:bg-[#5DA2D2] dark:hover:bg-[#378BC5]",
  selectionInactive:
    "bg-gray-100  dark:bg-gray-600 text-black dark:text-gray-400 hover:bg-[#a4d1f4] dark:hover:bg-[#378BC5] hover:dark:text-black hover:font-semibold",
  selectionDisabled:
    "bg-gray-100 text-[#ADADAD] dark:bg-[#3E3E3E] dark:text-gray-500 cursor-not-allowed",
};
// Added flex to all buttons and have centered all content.
const buttonVariants = cva("flex justify-center items-center", {
  variants: {
    variant,
    variantToggle,
    size: {
      default: "",
      icon_sm: "h-4, w-fit px-1",
      icon_lg: "h-[35px] w-[35px]",
      icon_tooltip: "w-full",
      button_sm: "min-w-[6rem] w-fit h-4 text-xs",
      button_sm_medium: "min-w-[6rem] w-fit h-5 text-sm",
      button_sm_large: "min-w-[7rem] w-fit h-6 text-base",
      button_sm_mainBlueAction: "min-w-[7rem] w-fit h-4 text-xs",
      button_md: "min-w-[8rem] w-fit p-1 px-3",
      // button_md_CP: "min-w-[6rem] w-fit py-0.5 ",
      button_md_CP: "w-14  ",
      button_lg: "min-w-[8rem] w-fit h-6 p-1 px-3", // Unsure if this is getting used?
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export const extractVariantClasses = () => {
  return Object.keys(variant).map((variantKey) => {
    return variantKey;
  });
};

export const extractVariantToggleClasses = () => {
  return Object.keys(variantToggle).map((variantKey) => {
    return variantKey;
  });
};

// Get the list of variant strings
export const buttonVariantClasses = extractVariantClasses();
export const buttonVariantToggleClasses = extractVariantToggleClasses();

//   for Typescript:
export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  href?: string;
  variant?: string | null | undefined;
  variantToggle?: string | null | undefined;
}

//   for Typescript:
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, href, variant, variantToggle, size, ...props }, ref) => {
    if (href) {
      return (
        <a
          href={href}
          className={concatenateClassnames(
            buttonVariants({ variant, variantToggle, size, className }),
          )}
        ></a>
      );
    }
    return (
      <button
        className={concatenateClassnames(
          buttonVariants({ variant, variantToggle, size, className }),
        )}
        ref={ref}
        {...props}
      ></button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
